import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

function qs(params = {}) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "" && v !== false)
  );
  return new URLSearchParams(clean).toString();
}

const REVIEW_KEYS = ["reviews", "admin-reviews", "product", "products", "collection"];

function useInvalidateReviews() {
  const qc = useQueryClient();
  return () => REVIEW_KEYS.forEach((key) => qc.invalidateQueries({ queryKey: [key] }));
}

export function useReviews(handle, params = {}) {
  return useQuery({
    queryKey: ["reviews", handle, params],
    queryFn: async () => (await api.get(`/products/${handle}/reviews?${qs(params)}`)).data,
    enabled: !!handle,
    placeholderData: (prev) => prev,
  });
}

export function useSubmitReview(handle) {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: async (payload) => (await api.post(`/products/${handle}/reviews`, payload)).data,
    onSuccess: invalidate,
  });
}

export function useAdminReviews(params = {}) {
  return useQuery({
    queryKey: ["admin-reviews", params],
    queryFn: async () => (await api.get(`/admin/reviews?${qs(params)}`)).data,
    placeholderData: (prev) => prev,
  });
}

export function useReviewModeration() {
  const invalidate = useInvalidateReviews();
  const opts = { onSuccess: invalidate };
  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/admin/reviews/${id}/status`, { status })).data,
    ...opts,
  });
  const reply = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/admin/reviews/${id}/reply`, { body })).data,
    ...opts,
  });
  const removeReply = useMutation({
    mutationFn: async ({ id }) => (await api.delete(`/admin/reviews/${id}/reply`)).data,
    ...opts,
  });
  const remove = useMutation({
    mutationFn: async ({ id, handle }) => (await api.delete(`/products/${handle}/reviews/${id}`)).data,
    ...opts,
  });
  return { setStatus, reply, removeReply, remove };
}

export async function uploadReviewPhoto(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/uploads/review-photo", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
